/* Feb-2026 · "Mei" intro video — the landing hero's video card must
 * always show Crown & Rose's own welcome/introduction recording,
 * never one of the community-submitted "instruction" videos from the
 * admin-approved landing-intro-videos queue (that backend feed still
 * exists for other uses, but nothing on the frontend reads from it
 * any more — this card is fully independent of it). The video URL
 * and poster are plain CMS fields (`mei_intro_video_url` /
 * `mei_intro_poster_url`) set once from the admin Landing editor —
 * until a real file is set, this renders Mei's desk portrait with a
 * "coming soon" tag instead of ever guessing at a fallback video.
 *
 * 2026-02 (later) — no autoplay/ambient loop: the poster sits on top
 * until the visitor explicitly presses play, the clip plays once with
 * sound, and ending returns to the poster rather than looping or
 * resuming a muted background loop. Same behaviour on every device.
 *
 * 2026-02 (lightbox) — clicking play expands the video into a centred
 * fullscreen overlay so it isn't cramped by the sidebar column.  A ×
 * button in the upper-right corner (and the Escape key) close the
 * overlay and return to the poster thumbnail; clicking the dark
 * backdrop also closes.  The carousel columns underneath remain fully
 * accessible — the overlay sits in a React portal on document.body and
 * does not destroy or replace any sibling DOM.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Play, Captions, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FONTS, P } from './scriptorium/palette';

const MEI_POSTER_DEFAULT = '/assets/mei-intro-poster.jpeg';
const MEI_VIDEO_DEFAULT = '/assets/mei-intro-video.mp4';
const SUPPORTED_CAPTION_LANGS = ['en', 'es', 'fr', 'de', 'pt', 'zh'];
const CAPTION_LABELS = { en: 'English', es: 'Español', fr: 'Français', de: 'Deutsch', pt: 'Português', zh: '中文' };

export default function WelcomeVideoBand({ content, compact = false, localeVideoUrl = null }) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded]   = useState(false); // lightbox open
  const [playing,  setPlaying]    = useState(false); // video element mounted
  const [ccOn,     setCcOn]       = useState(true);
  const ref = useRef(null);

  const videoUrl = localeVideoUrl || content?.mei_intro_video_url || null;
  const poster   = content?.mei_intro_poster_url || MEI_POSTER_DEFAULT;

  const isEnglish    = (i18n.language || 'en').toLowerCase().startsWith('en');
  const rawLang      = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const captionLang  = SUPPORTED_CAPTION_LANGS.includes(rawLang) ? rawLang : 'en';

  let videoPathname = videoUrl;
  try { videoPathname = videoUrl ? new URL(videoUrl, window.location.origin).pathname : null; } catch { /* keep raw */ }
  const isDefaultVideo = !videoUrl || videoPathname === MEI_VIDEO_DEFAULT;
  const captionsUrl    = content?.mei_intro_captions_url
    || (isDefaultVideo ? `/assets/mei-intro-captions.${captionLang}.vtt` : null);
  const title = (isEnglish && content?.mei_intro_title) || t('landing.welcomeVideoEyebrow');

  // ── Open lightbox + start playing ──────────────────────────────────────
  const openLightbox = () => {
    if (!videoUrl) return;
    setExpanded(true);
    setPlaying(true);
  };

  // ── Close lightbox + stop ───────────────────────────────────────────────
  const closeLightbox = () => {
    setExpanded(false);
    setPlaying(false);
    if (ref.current) { ref.current.pause(); ref.current.currentTime = 0; }
  };

  // Drive actual play() call once <video> mounts inside the portal
  useEffect(() => {
    if (!playing || !ref.current) return;
    ref.current.currentTime = 0;
    ref.current.play().catch(() => setPlaying(false));
  }, [playing]);

  // CC track mode
  useEffect(() => {
    const tt = ref.current?.textTracks?.[0];
    if (tt) tt.mode = ccOn ? 'showing' : 'hidden';
  }, [ccOn, playing, captionsUrl]);

  // Escape key closes the lightbox
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Lightbox portal ─────────────────────────────────────────────────────
  const lightbox = expanded ? ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
      data-testid="video-lightbox-backdrop"
      onClick={closeLightbox}
    >
      {/* Lightbox frame — click inside doesn't close */}
      <div
        className="relative w-full max-w-4xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-lightbox-frame"
      >
        {/* Close button — upper-right of the video frame */}
        <button
          type="button"
          onClick={closeLightbox}
          data-testid="video-lightbox-close"
          aria-label="Close video"
          className="absolute -top-10 right-0 z-10 flex h-8 w-8 items-center justify-center rounded-full border border-white/30 bg-black/60 text-white transition hover:bg-black/90"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Video */}
        <div
          className="aspect-video w-full overflow-hidden rounded-xl border-2 shadow-2xl"
          style={{ borderColor: `${P.gold}aa` }}
        >
          {playing && (
            <video
              ref={ref}
              src={videoUrl}
              poster={poster}
              controls
              playsInline
              preload="metadata"
              onEnded={closeLightbox}
              className="h-full w-full object-cover"
              data-testid="landing-welcome-video"
            >
              {captionsUrl && (
                <track
                  key={captionsUrl}
                  kind="captions"
                  src={captionsUrl}
                  srcLang={captionLang}
                  label={CAPTION_LABELS[captionLang] || 'English'}
                  default
                />
              )}
            </video>
          )}
        </div>

        {/* CC toggle — visible while playing */}
        {captionsUrl && playing && (
          <button
            type="button"
            onClick={() => setCcOn((v) => !v)}
            data-testid="landing-welcome-video-cc-toggle"
            aria-pressed={ccOn}
            className={`absolute bottom-3 right-3 z-10 flex items-center gap-1 rounded-sm border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors ${ccOn ? 'border-[#f4d98b] bg-black/70 text-[#f4d98b]' : 'border-white/40 bg-black/50 text-white/70'}`}
          >
            <Captions className="h-3.5 w-3.5" /> CC
          </button>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  // ── Thumbnail (always rendered in the sidebar) ──────────────────────────
  return (
    <div className={compact ? 'flex h-full flex-col' : 'mx-auto max-w-5xl'} data-testid="landing-welcome-video-section">
      <div className={compact ? 'mb-3' : 'mb-6 max-w-2xl'}>
        <div className="text-[10px] uppercase tracking-[0.26em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>{title}</div>
      </div>

      {/* Thumbnail frame */}
      <div
        className={`relative w-full overflow-hidden rounded-lg border-2 shadow-xl ${compact ? 'aspect-video lg:aspect-square flex-1' : 'mx-auto aspect-video md:w-3/4'}`}
        style={{ borderColor: `${P.gold}aa`, backgroundColor: '#071512', outline: `1px solid ${P.goldDeep}44`, outlineOffset: '5px' }}
        data-testid="landing-welcome-video-frame"
      >
        {/* Poster always visible in the thumbnail */}
        <img
          src={poster}
          alt={t('landing.welcomeVideoPosterAlt')}
          className="h-full w-full object-cover"
          data-testid="landing-welcome-video-poster-only"
        />

        {/* "Now Playing" shimmer when lightbox is open */}
        {expanded && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/50">
            <div className="flex gap-1">
              {[0,1,2].map(i => (
                <span
                  key={i}
                  className="inline-block h-5 w-1 rounded-full bg-[#f4d98b] animate-pulse"
                  style={{ animationDelay: `${i * 0.15}s` }}
                />
              ))}
            </div>
            <span className="text-[11px] font-semibold text-white/80 tracking-wide">Now Playing</span>
          </div>
        )}

        {/* Play button — only when not already expanded */}
        {videoUrl && !expanded && (
          <button
            type="button"
            onClick={openLightbox}
            data-testid="landing-welcome-video-play"
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/10 text-white transition-[background-color] hover:bg-black/30"
            aria-label={t('landing.welcomeVideoPlayAria', { title })}
          >
            <span className="grid h-14 w-14 place-items-center rounded-full border-2 border-[#d7b96f] bg-black/45 shadow-xl">
              <Play className="ml-1 h-6 w-6 text-[#f4d98b]" />
            </span>
            <span className="rounded-sm bg-black/55 px-3 py-1 text-[13px] font-semibold" style={{ fontFamily: FONTS.display }}>
              {t('landing.welcomeVideoPlayWithSound')}
            </span>
          </button>
        )}

        {/* Coming-soon badge when no video URL is set */}
        {!videoUrl && (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center bg-gradient-to-t from-black/60 to-transparent p-3">
            <span className="rounded-sm bg-black/55 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-white/85">
              {t('landing.welcomeVideoComingSoon')}
            </span>
          </div>
        )}
      </div>

      {/* Render lightbox into document.body so it escapes the sticky column */}
      {lightbox}
    </div>
  );
}
